/**
 * Inactivity: how long it has been since the PERSON last did something.
 *
 * "Did something" is an INTERACTION (owner, 2026-10-28): a click or tap, a key, or text entered
 * (typing, pasting). Moving the mouse, turning the wheel and scrolling do not count — an
 * unattended screen with a hand on the mouse is still unattended. Background traffic
 * (react-query refetches, the live-event stream, polls) never counts either: it is not recorded
 * here, and it is not allowed to refresh the session (see `mayRefresh`).
 *
 * The server's rule is the enforcement: a refresh more than `SESSION_IDLE_MINUTES` after the
 * session's last refresh or sign-in ends the session. This module is what keeps an active person
 * inside it (the SPA refreshes only while they interact) and what tells an idle one, before the
 * server does, that they are about to be signed out.
 *
 * Two facts are kept in `localStorage`, so every tab of the browser reads the same ones and
 * agrees: when the person last interacted (in any tab), and when the session was last refreshed
 * or signed in. A storage event only makes the other tabs notice sooner; each tab also reads
 * the values on its own tick. Times are `Date.now()` because that is the one clock tabs share;
 * a clock that jumps back is clamped, never trusted into a longer session.
 */

const MINUTE_MS = 60_000;
const DEFAULT_IDLE_MINUTES = 15;
/** The warning opens this long before the limit. */
const WARNING_LEAD_MS = 2 * MINUTE_MS;
/** Interactions closer together than this are one write. */
const THROTTLE_MS = 1_000;
const TICK_MS = 1_000;

export const INTERACTION_KEY = 'qmi.idle.interaction';
export const REFRESH_KEY = 'qmi.idle.refresh';

/** Events that mean the person interacted (captured on any element): a click or tap, a key, or
 *  text entered. Not a pointer move, a wheel turn, a touch that only scrolls, or `scroll`. */
export const INTERACTION_EVENTS = ['click', 'keydown', 'input'] as const;

/**
 * The limit, in minutes. It mirrors the server's `SESSION_IDLE_MINUTES` (15): a developer who
 * lengthens it there sets `VITE_SESSION_IDLE_MINUTES` to the same value; nothing else changes it.
 */
export function idleMinutes(configured: unknown = import.meta.env?.VITE_SESSION_IDLE_MINUTES): number {
  const minutes = Number(configured);
  return Number.isInteger(minutes) && minutes >= 1 ? minutes : DEFAULT_IDLE_MINUTES;
}

export type IdlePhase = 'active' | 'warning' | 'idle';

export interface IdleStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface IdleEnvironment {
  now(): number;
  /** The shared store; null when the browser has none (then nothing is shared or remembered). */
  storage(): IdleStorage | null;
  limitMs: number;
}

/** What a page listens on (the window). */
export interface IdleTarget {
  addEventListener(type: string, listener: (event: Event) => void, options?: unknown): void;
  removeEventListener(type: string, listener: (event: Event) => void, options?: unknown): void;
}

export interface IdleWatchHandlers {
  /** Called on every tick with where the person stands; `secondsLeft` is until sign-out. */
  onTick(phase: IdlePhase, secondsLeft: number): void;
  /** Called once when the limit is reached. */
  onIdle(): void;
  /** True when the sign-in itself is gone from the browser (another tab signed out). Optional. */
  signedOut?(): boolean;
}

export interface Inactivity {
  /** A sign-in: both facts are now. */
  markSignedIn(): void;
  /** The person did something. Throttled unless `force`. */
  markInteraction(force?: boolean): void;
  /** The session was refreshed; `at` is when the request started. */
  markRefreshed(at?: number): void;
  /** Whether the person has done anything since the session was last refreshed. */
  mayRefresh(): boolean;
  /** When the session was last refreshed or signed in, on THIS browser's clock; null if unknown. */
  lastRefreshAt(): number | null;
  idleMs(): number;
  phase(): IdlePhase;
  secondsLeft(): number;
  /** Forget both facts (sign-out). */
  clear(): void;
  /** Listen for the person and for the other tabs; stop with the returned function. */
  watch(target: IdleTarget, timers: IdleTimers, handlers: IdleWatchHandlers): () => void;
}

export interface IdleTimers {
  setInterval(handler: () => void, ms: number): unknown;
  clearInterval(handle: unknown): void;
}

function readTime(storage: IdleStorage | null, key: string): number | null {
  try {
    const raw = storage?.getItem(key);
    if (raw === null || raw === undefined) return null;
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
  } catch {
    return null;
  }
}

function writeTime(storage: IdleStorage | null, key: string, value: number): void {
  try {
    storage?.setItem(key, String(value));
  } catch {
    // A browser that refuses to remember leaves the server's rule as the only one.
  }
}

export function createInactivity(env: IdleEnvironment): Inactivity {
  let lastWrite = Number.NEGATIVE_INFINITY;

  /** When the person last interacted. A value in the future (the clock went back) is clamped
   *  to now; an unknown one starts the count now. */
  const lastInteraction = (): number => {
    const now = env.now();
    const stored = readTime(env.storage(), INTERACTION_KEY);
    if (stored === null || stored > now) {
      writeTime(env.storage(), INTERACTION_KEY, now);
      return now;
    }
    return stored;
  };

  const idleMs = (): number => Math.max(0, env.now() - lastInteraction());

  const phaseOf = (idle: number): IdlePhase => {
    if (idle >= env.limitMs) return 'idle';
    const lead = Math.min(WARNING_LEAD_MS, env.limitMs / 2);
    return idle >= env.limitMs - lead ? 'warning' : 'active';
  };

  const secondsLeft = (): number => Math.max(0, Math.ceil((env.limitMs - idleMs()) / 1000));

  const markInteraction = (force = false): void => {
    const now = env.now();
    if (!force && now - lastWrite < THROTTLE_MS && now >= lastWrite) return;
    lastWrite = now;
    writeTime(env.storage(), INTERACTION_KEY, now);
  };

  return {
    markSignedIn(): void {
      const now = env.now();
      lastWrite = now;
      writeTime(env.storage(), INTERACTION_KEY, now);
      writeTime(env.storage(), REFRESH_KEY, now);
    },

    markInteraction,

    markRefreshed(at?: number): void {
      writeTime(env.storage(), REFRESH_KEY, Math.min(at ?? env.now(), env.now()));
    },

    mayRefresh(): boolean {
      const interaction = readTime(env.storage(), INTERACTION_KEY);
      const refreshed = readTime(env.storage(), REFRESH_KEY);
      // Nothing recorded (a session from before this existed): the server's rule decides.
      if (interaction === null || refreshed === null) return true;
      return interaction > refreshed;
    },

    lastRefreshAt: () => readTime(env.storage(), REFRESH_KEY),

    idleMs,
    phase: () => phaseOf(idleMs()),
    secondsLeft,

    clear(): void {
      try {
        env.storage()?.removeItem(INTERACTION_KEY);
        env.storage()?.removeItem(REFRESH_KEY);
      } catch {
        // Nothing to forget.
      }
    },

    watch(target, timers, handlers): () => void {
      let ended = false;
      // Once this tab has seen the interaction fact, its absence means another tab signed out
      // (clear) - never "start counting now", which would keep the account on screen.
      let seen = readTime(env.storage(), INTERACTION_KEY) !== null;
      const end = (): void => {
        ended = true;
        handlers.onIdle();
      };
      const check = (): void => {
        if (ended) return;
        if (seen && readTime(env.storage(), INTERACTION_KEY) === null) {
          handlers.onTick('idle', 0);
          end();
          return;
        }
        if (handlers.signedOut?.()) {
          handlers.onTick('idle', 0);
          end();
          return;
        }
        const idle = idleMs();
        seen = seen || readTime(env.storage(), INTERACTION_KEY) !== null;
        const phase = phaseOf(idle);
        handlers.onTick(phase, secondsLeft());
        if (phase === 'idle') end();
      };
      const onInteraction = (): void => markInteraction();
      // Another tab wrote or removed a fact (or the stored sign-in): look again now instead of
      // on the next tick.
      const onStorage = (): void => check();
      for (const type of INTERACTION_EVENTS) {
        target.addEventListener(type, onInteraction, { capture: true, passive: true });
      }
      target.addEventListener('storage', onStorage);
      // A tab that was asleep is looked at the moment it wakes.
      target.addEventListener('visibilitychange', check);
      target.addEventListener('focus', check);
      const handle = timers.setInterval(check, TICK_MS);
      return () => {
        ended = true;
        timers.clearInterval(handle);
        for (const type of INTERACTION_EVENTS) {
          target.removeEventListener(type, onInteraction, { capture: true });
        }
        target.removeEventListener('storage', onStorage);
        target.removeEventListener('visibilitychange', check);
        target.removeEventListener('focus', check);
      };
    },
  };
}

function browserStorage(): IdleStorage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

/** The one the app uses. */
export const inactivity: Inactivity = createInactivity({
  now: () => Date.now(),
  storage: browserStorage,
  limitMs: idleMinutes() * MINUTE_MS,
});

/** Seconds as the minutes and seconds a clock shows: 119 -> "1:59". */
export function formatCountdown(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  const rest = String(whole % 60).padStart(2, '0');
  return `${Math.floor(whole / 60)}:${rest}`;
}

/** Renew when the access token has less than this left. */
export const RENEW_LEAD_MS = 2 * MINUTE_MS;
/** After a renewal that could not be made (no network), wait this long to try again. */
export const RENEW_RETRY_MS = 15_000;

/**
 * Whether the session should be renewed now: the person has done something since the last
 * renewal, the access token is about to end (or has), and the last attempt was not just now.
 *
 * The token's end is worked out on THIS browser's clock: when the session was last refreshed
 * (stored by this browser) plus the token's lifetime (`exp - iat`, a length of time). The
 * server's `exp` is never compared with `now`, so a client clock that is slow or fast neither
 * misses the renewal nor renews on every interaction. `lifetimeMs` or `refreshedAt` null (not
 * readable): then only a refused request renews.
 */
export function renewalDue(facts: {
  mayRefresh: boolean;
  now: number;
  refreshedAt: number | null;
  lifetimeMs: number | null;
  lastAttemptAt: number;
}): boolean {
  if (!facts.mayRefresh || facts.refreshedAt === null || facts.lifetimeMs === null) return false;
  // A refresh time in the future (the clock went back) counts as now.
  const tokenEndsAt = Math.min(facts.refreshedAt, facts.now) + facts.lifetimeMs;
  if (tokenEndsAt - facts.now > RENEW_LEAD_MS) return false;
  return facts.now - facts.lastAttemptAt >= RENEW_RETRY_MS;
}
